import React, { useEffect, useMemo, useState } from 'react';
import { Article } from '../types/api.types';
import { fetchSimilarItems, createOutstockDeclaration } from '../services/outstockService';
import { UI_SETTINGS } from '../config/api';
import { OutstockPreviewSeries, OutstockPreviewResponse, OutstockDataPoint } from '../types/outstock';
import ArticleSelector from './ArticleSelector';
import { ChevronDown, ChevronRight } from 'lucide-react';
import api from '../services/api';
import { API_ROUTES } from '../config/api';

type Step = 'similar' | 'period' | 'preview' | 'confirm';

interface OutstockModalProps {
  open: boolean;
  onClose: () => void;
  databaseId: string;
  article: Article;
  selectedModelName?: string | null;
}

const OutstockModal: React.FC<OutstockModalProps> = ({ open, onClose, databaseId, article, selectedModelName }) => {
  const [step, setStep] = useState<Step>('similar');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  interface SimilarItem {
    id: string;
    score: number;
    name?: string;
  }

  const [suggested, setSuggested] = useState<SimilarItem[]>([]);
  const [selected, setSelected] = useState<SimilarItem[]>([]);

  const [manualArticles, setManualArticles] = useState<Article[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Dropdown states
  const [suggestedOpen, setSuggestedOpen] = useState(true);
  const [manualOpen, setManualOpen] = useState(false);
  const [selectedOpen, setSelectedOpen] = useState(true);

  // Confirmation dialog for closing
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const allSelectedIds = useMemo(() => new Set(selected.map((s: SimilarItemWithScore) => s.id)), [selected]);

  useEffect(() => {
    if (!open) return;
    setStep('similar');
    setError(null);
    setLoading(true);
    setSuggested([]);
    setSelected([]);
    setManualArticles([]);
    setStartDate('');
    setEndDate('');
    setSuggestedOpen(true);
    setManualOpen(false);
    setSelectedOpen(true);
    setShowCloseConfirm(false);
    (async () => {
      try {
        const data = await fetchSimilarItems(String(databaseId), String(article.id), UI_SETTINGS.OUTSTOCK.DEFAULT_SIMILAR_ITEMS);
        const key = String(article.id);
        const pairs = data?.results?.[key] || [];
        const mapped = pairs.map(([id, score, name]: [string, number, string]) => ({ id, score, name }));
        setSuggested(mapped);
        setSelected(mapped); // preselect all
      } catch (e: any) {
        setError('Impossible de récupérer les articles similaires.');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, databaseId, article.id]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        // If we're on the final confirmation step, close directly without confirmation
        if (step === 'confirm') {
          onClose();
        } else {
          setShowCloseConfirm(true);
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, step, onClose]);

  const handleCloseAttempt = () => {
    // If we're on the final confirmation step, close directly without confirmation
    if (step === 'confirm') {
      onClose();
    } else {
      setShowCloseConfirm(true);
    }
  };

  const confirmClose = () => {
    setShowCloseConfirm(false);
    onClose();
  };

  const cancelClose = () => {
    setShowCloseConfirm(false);
  };

  interface SimilarItemWithScore {
    id: string;
    score: number;
  }

  const handleInclude = (id: string): void => {
    const found = suggested.find((s: SimilarItemWithScore) => s.id === id);
    if (found && !allSelectedIds.has(id)) {
      setSelected(prev => [...prev, found]);
    }
  };

  const handleExclude = (id: string): void => {
    setSelected(prev => prev.filter((s: SimilarItemWithScore) => s.id !== id));
  };

  const [preview, setPreview] = useState<OutstockPreviewResponse | null>(null);
  const [corrections, setCorrections] = useState<Record<string, Record<string, number>>>({});
  const [mainArticlePredictions, setMainArticlePredictions] = useState<Array<{date: string, value: number}>>([]);

  const goToPreview = async () => {
    setError(null);
    setLoading(true);
    try {
      // Fetch first available model for main article display (backend uses SKU-wise for corrected values)
      let modelToUse: string = selectedModelName || '';
      if (!modelToUse) {
        try {
          const modelsResponse = await api.get(
            `${API_ROUTES.PREDICTION.BASE}${API_ROUTES.PREDICTION.AVAILABLE_MODELS(databaseId)}`
          );
          const models = modelsResponse.data || [];
          const firstWithPredictions = models.find((m: any) => m.has_predictions);
          modelToUse = firstWithPredictions?.id || firstWithPredictions?.name || models[0]?.id || 'sarima';
        } catch (e) {
          console.warn('Could not fetch models, using default:', e);
          modelToUse = 'sarima';
        }
      }

      // Fetch main article predictions (for display; backend uses best model per article for corrected values)
      try {
        const predictionsResponse = await api.post(
          `${API_ROUTES.PREDICTION.BASE}${API_ROUTES.PREDICTION.PREDICTION_DATA(databaseId, modelToUse)}`,
          {
            model_name: modelToUse,
            filters: { article_ids: [String(article.id)] }
          }
        );
        
        if (predictionsResponse.data?.predictions) {
          // Filter predictions by date range and use breakdown.base_value for raw/base value
          const filtered = predictionsResponse.data.predictions
            .filter((p: any) => {
              const predDate = new Date(p.date);
              const start = new Date(startDate);
              const end = new Date(endDate);
              return predDate >= start && predDate <= end;
            })
            .map((p: any) => ({
              date: p.date,
              // Use breakdown.base_value for base prediction value (without manual corrections)
              value: p.breakdown?.base_value !== undefined ? p.breakdown.base_value : (p.sales || 0)
            }));
          setMainArticlePredictions(filtered);
        }
      } catch (e) {
        console.warn('Could not fetch main article predictions:', e);
        setMainArticlePredictions([]);
      }

      // Fetch substitute data (no model_name = backend uses best model per article, SKU-wise)
      const response = await api.post(API_ROUTES.OUTSTOCK.BASE + API_ROUTES.OUTSTOCK.CORRECTED_VALUES(String(databaseId)), {
        product_id: String(article.id),
        start_date: startDate,
        end_date: endDate,
        substitutes: selected.map(s => [s.id, s.score]),
      });
      setPreview(response.data);
      // Initialize corrections from the preview data
      const initialCorrections: Record<string, Record<string, number>> = {};
      response.data.results.forEach((series: OutstockPreviewSeries) => {
        if (!series.is_main) {
          initialCorrections[series.article_id] = {};
          series.corrected.forEach((point: OutstockDataPoint, idx: number) => {
            const originalPoint = series.original[idx];
            if (originalPoint) {
              // Use impact if provided, otherwise calculate from corrected - original
              const impact = point.impact !== undefined 
                ? point.impact 
                : (point.value - originalPoint.value);
              initialCorrections[series.article_id][originalPoint.date] = impact;
            }
          });
        }
      });
      setCorrections(initialCorrections);
      setStep('preview');
    } catch (e: any) {
      setError("Erreur lors de la génération de l'aperçu.");
    } finally {
      setLoading(false);
    }
  };

  const handleCorrectionChange = (articleId: string, date: string, value: number) => {
    setCorrections(prev => {
      const newCorrections = { ...prev };
      if (!newCorrections[articleId]) {
        newCorrections[articleId] = {};
      }
      newCorrections[articleId][date] = value;
      return newCorrections;
    });
  };

  const handleReset = () => {
    // Reset corrections to original values from preview
    if (preview) {
      const resetCorrections: Record<string, Record<string, number>> = {};
      preview.results.forEach((series: OutstockPreviewSeries) => {
        if (!series.is_main) {
          resetCorrections[series.article_id] = {};
          series.corrected.forEach((point: OutstockDataPoint, idx: number) => {
            const originalPoint = series.original[idx];
            if (originalPoint) {
              // Use impact if provided, otherwise calculate from corrected - original
              const impact = point.impact !== undefined 
                ? point.impact 
                : (point.value - originalPoint.value);
              resetCorrections[series.article_id][originalPoint.date] = impact;
            }
          });
        }
      });
      setCorrections(resetCorrections);
    }
  };

  // Helper function to format date as abbreviated month (nov-25)
  const formatMonthAbbr = (dateStr: string): string => {
    const date = new Date(dateStr);
    const months = ['janv', 'févr', 'mars', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc'];
    const month = months[date.getMonth()];
    const year = date.getFullYear().toString().slice(-2);
    return `${month}-${year}`;
  };

  // Helper function to get all unique dates from preview data
  const getAllDates = (): string[] => {
    if (!preview || preview.results.length === 0) return [];
    const datesSet = new Set<string>();
    preview.results.forEach(series => {
      series.original.forEach(point => datesSet.add(point.date));
    });
    return Array.from(datesSet).sort();
  };

  // Handle step navigation (only allow going back)
  const handleStepClick = (targetStep: Step) => {
    const stepOrder: Step[] = ['similar', 'period', 'preview', 'confirm'];
    const currentIndex = stepOrder.indexOf(step);
    const targetIndex = stepOrder.indexOf(targetStep);
    
    // Only allow navigation to previous steps
    if (targetIndex < currentIndex) {
      setStep(targetStep);
      setError(null); // Clear any errors when navigating back
    }
  };

  // Check if a step is clickable (can navigate back to it)
  const isStepClickable = (targetStep: Step): boolean => {
    const stepOrder: Step[] = ['similar', 'period', 'preview', 'confirm'];
    const currentIndex = stepOrder.indexOf(step);
    const targetIndex = stepOrder.indexOf(targetStep);
    return targetIndex < currentIndex;
  };

  const handleConfirm = async () => {
    setError(null);
    setLoading(true);
    try {
      // First create the outstock declaration
      const declaration = await createOutstockDeclaration(String(databaseId), {
        product_id: String(article.id),
        start_date: startDate || new Date().toISOString().slice(0, 10),
        end_date: endDate || new Date().toISOString().slice(0, 10),
        products: selected.map(s => [s.id, s.score])
      });

      // Then create corrections for each substitute and date
      if (preview) {
        const correctionPromises: Promise<any>[] = [];
        
        // For each substitute article (skip main article if present)
        preview.results.filter(series => !series.is_main).forEach(series => {
          const articleCorrections = corrections[series.article_id];
          if (articleCorrections) {
            // For each date that has a correction
            Object.entries(articleCorrections).forEach(([date, impact]) => {
              correctionPromises.push(
                api.post(API_ROUTES.OUTSTOCK.BASE + API_ROUTES.OUTSTOCK.CORRECTIONS(String(databaseId)), {
                  article_id: series.article_id,
                  correction_date: date,
                  quantity: impact,
                  declaration_id: declaration.id
                })
              );
            });
          }
        });

        await Promise.all(correctionPromises);
      }

      setStep('confirm');
    } catch (e: any) {
      setError("La sauvegarde de la déclaration a échoué.");
      console.error('Error saving outstock declaration:', e);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30" onClick={handleCloseAttempt}>
        <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
            <h3 className="text-lg font-semibold">Déclarer une rupture — {article.name || article.id}</h3>
            <button onClick={handleCloseAttempt} className="text-gray-500 hover:text-gray-700">✕</button>
          </div>

          <div className="px-6 py-4 flex-1 overflow-auto">
            {error && (
              <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                {error}
              </div>
            )}

            {/* Steps indicator */}
            <div className="flex items-center gap-3 mb-4 text-sm">
              <div 
                className={`px-2 py-1 rounded ${step==='similar'?'bg-blue-600 text-white':'bg-gray-100'} ${isStepClickable('similar') ? 'cursor-pointer hover:bg-gray-200 transition-colors' : ''}`}
                onClick={() => handleStepClick('similar')}
              >
                Articles similaires
              </div>
              <div>→</div>
              <div 
                className={`px-2 py-1 rounded ${step==='period'?'bg-blue-600 text-white':'bg-gray-100'} ${isStepClickable('period') ? 'cursor-pointer hover:bg-gray-200 transition-colors' : ''}`}
                onClick={() => handleStepClick('period')}
              >
                Période
              </div>
              <div>→</div>
              <div 
                className={`px-2 py-1 rounded ${step==='preview'?'bg-blue-600 text-white':'bg-gray-100'} ${isStepClickable('preview') ? 'cursor-pointer hover:bg-gray-200 transition-colors' : ''}`}
                onClick={() => handleStepClick('preview')}
              >
                Aperçu
              </div>
              <div>→</div>
              <div 
                className={`px-2 py-1 rounded ${step==='confirm'?'bg-blue-600 text-white':'bg-gray-100'} ${isStepClickable('confirm') ? 'cursor-pointer hover:bg-gray-200 transition-colors' : ''}`}
                onClick={() => handleStepClick('confirm')}
              >
                Confirmation
              </div>
            </div>

            {step === 'similar' && (
              <div className="space-y-4">
                {/* Suggested items dropdown */}
                <div className="border rounded">
                  <button 
                    className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-t"
                    onClick={() => setSuggestedOpen(!suggestedOpen)}
                  >
                    <span className="font-medium">Proposés ({suggested.length})</span>
                    {suggestedOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  {suggestedOpen && (
                    <div className="p-4 max-h-48 overflow-auto">
                      <div className="grid grid-cols-2 gap-2">
                        {suggested.map(s => (
                          <div key={s.id} className="flex items-center justify-between border rounded px-3 py-2">
                            <div>
                              <div className="font-medium text-sm">{s.id}</div>
                              {s.name && <div className="text-xs text-gray-600">{s.name}</div>}
                              <div className="text-xs text-gray-500">Score: {s.score.toFixed(2)}</div>
                            </div>
                            {allSelectedIds.has(s.id) ? (
                              <button className="text-sm text-red-600" onClick={() => handleExclude(s.id)}>Exclure</button>
                            ) : (
                              <button className="text-sm text-blue-600" onClick={() => handleInclude(s.id)}>Inclure</button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Manual add dropdown */}
                <div className="border rounded">
                  <button 
                    className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-t"
                    onClick={() => setManualOpen(!manualOpen)}
                  >
                    <span className="font-medium">Ajouter manuellement ({manualArticles.length})</span>
                    {manualOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  {manualOpen && (
                    <div className="p-4">
                      <ArticleSelector 
                        onSelectionChange={() => {}}
                        onApplyFilters={() => {}}
                        onArticlesChange={(arts) => {
                          setManualArticles(arts);
                        }}
                      />
                      {manualArticles.length > 0 && (
                        <div className="mt-3 max-h-48 overflow-auto">
                          <div className="grid grid-cols-2 gap-2">
                            {manualArticles.map(a => (
                              <div key={a.id} className="flex items-center justify-between border rounded px-3 py-2">
                                <div className="font-medium text-sm">{a.name || a.id}</div>
                                {allSelectedIds.has(String(a.id)) ? (
                                  <button className="text-sm text-red-600" onClick={() => handleExclude(String(a.id))}>Exclure</button>
                                ) : (
                                  <button className="text-sm text-blue-600" onClick={() => setSelected(prev => [...prev, { id: String(a.id), score: 0.5, name: a.name }])}>Inclure</button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected items dropdown */}
                <div className="border rounded">
                  <button 
                    className="w-full px-4 py-3 flex items-center justify-between bg-blue-50 hover:bg-blue-100 rounded-t"
                    onClick={() => setSelectedOpen(!selectedOpen)}
                  >
                    <span className="font-medium text-blue-800">Sélectionnés ({selected.length})</span>
                    {selectedOpen ? <ChevronDown size={16} className="text-blue-800" /> : <ChevronRight size={16} className="text-blue-800" />}
                  </button>
                  {selectedOpen && (
                    <div className="max-h-48 overflow-auto">
                      {selected.length === 0 ? (
                        <div className="p-4 text-gray-500 text-sm text-center">Aucun article sélectionné</div>
                      ) : (
                        <ul className="divide-y">
                          {selected.map(s => (
                            <li key={s.id} className="flex items-center justify-between px-4 py-3">
                              <div className="text-sm">
                                <span>{s.id}</span>
                                {s.name && <span className="mx-1 text-gray-600">{s.name}</span>}
                                <span className="text-gray-500">• {s.score.toFixed(2)}</span>
                              </div>
                              <button className="text-sm text-red-600 hover:text-red-800" onClick={() => handleExclude(s.id)}>Retirer</button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

          {step === 'period' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Date de début</label>
                  <input type="date" className="border rounded px-3 py-2 w-full" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Date de fin</label>
                  <input type="date" className="border rounded px-3 py-2 w-full" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-2">Sélection actuelle ({selected.length})</h4>
                <div className="flex flex-wrap gap-2">
                  {selected.map(s => (
                    <div key={s.id} className="px-2 py-1 bg-gray-100 rounded text-sm">
                      <span>{s.id}</span>
                      {s.name && <span className="mx-1 text-gray-600">{s.name}</span>}
                      <span className="text-gray-500">• {s.score.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 'preview' && preview && (
            <div className="space-y-4">
              {/* Header Section */}
              <div className="border-b pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-600">Article en rupture</div>
                    <div className="text-lg font-semibold">{article.id} - {article.name || ''}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Période de rupture</div>
                    <div className="text-lg font-semibold">
                      {startDate && endDate 
                        ? `${new Date(startDate).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })} - ${new Date(endDate).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`
                        : 'Non définie'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Table Section */}
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        {/* Fixed left columns */}
                        <th className="sticky left-0 z-10 bg-gray-50 border-r border-b px-4 py-3 text-left min-w-[200px]">
                          Ventes prévisionnelles
                        </th>
                        <th className="sticky left-[200px] z-10 bg-gray-50 border-r border-b px-4 py-3 text-center bg-gray-50 font-semibold" colSpan={3}>Total</th>
                        {/* Scrollable monthly columns */}
                        {getAllDates().map((date, dateIndex) => (
                          <th key={date} colSpan={3} className={`border-b px-2 py-3 text-center bg-gray-50 ${dateIndex < getAllDates().length - 1 ? 'border-r-2 border-gray-300' : ''}`}>
                            {formatMonthAbbr(date)}
                          </th>
                        ))}
                      </tr>
                      {/* Main Article Row - Single value per month spanning all 3 columns */}
                      <tr className="bg-yellow-50">
                        <td className="sticky left-0 z-10 bg-yellow-50 border-r px-4 py-3 font-medium">
                          {article.id}
                        </td>
                        <td className="sticky left-[200px] z-10 bg-yellow-50 border-r px-4 py-3 text-right font-semibold" colSpan={3}>
                          {mainArticlePredictions.reduce((sum, p) => sum + p.value, 0).toFixed(0)}
                        </td>
                        {getAllDates().map((date, dateIndex) => {
                          const prediction = mainArticlePredictions.find(p => p.date === date);
                          const value = prediction?.value || 0;
                          const isLastMonth = dateIndex === getAllDates().length - 1;
                          return (
                            <td 
                              key={date} 
                              colSpan={3}
                              className={`px-2 py-2 text-center font-medium ${isLastMonth ? 'border-r' : 'border-r-2 border-r-gray-300'}`}
                            >
                              {value.toFixed(0)}
                            </td>
                          );
                        })}
                      </tr>
                      <tr className="bg-gray-50">
                        <th className="sticky left-0 z-10 bg-gray-50 border-r border-b">Articles de substitution</th>
                        <th className="sticky left-[200px] z-10 bg-gray-50 border-r border-b px-2 py-2 text-center text-xs font-normal min-w-[60px]">Actuel</th>
                        <th className="sticky left-[260px] z-10 bg-gray-50 border-r border-b px-2 py-2 text-center text-xs font-normal min-w-[60px]">Impact</th>
                        <th className="sticky left-[320px] z-10 bg-gray-50 border-r border-b px-2 py-2 text-center text-xs font-normal min-w-[60px]">Nouveau</th>
                        {getAllDates().map((date, dateIndex) => (
                          <React.Fragment key={date}>
                            <th className="border-b px-2 py-2 text-center text-xs font-normal">Actuel</th>
                            <th className="border-b px-2 py-2 text-center text-xs font-normal">Impact</th>
                            <th className={`border-b px-2 py-2 text-center text-xs font-normal ${dateIndex < getAllDates().length - 1 ? 'border-r-2 border-gray-300' : ''}`}>Nouveau</th>
                          </React.Fragment>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      

                      {/* Total Row (All Substitutes) */}
                      <tr className="bg-gray-100 font-semibold">
                        <td className="sticky left-0 z-10 bg-gray-100 border-r border-t px-4 py-3">
                          Total
                        </td>
                        {(() => {
                          const grandTotalActuel = preview.results
                            .filter(s => !s.is_main)
                            .reduce((sum, s) => sum + s.original.reduce((s2, p) => s2 + p.value, 0), 0);
                          const grandTotalImpact = Object.values(corrections).reduce(
                            (sum, articleCorrections) => sum + Object.values(articleCorrections).reduce((s, v) => s + v, 0),
                            0
                          );
                          const grandTotalNouveau = grandTotalActuel + grandTotalImpact;
                          return (
                            <React.Fragment>
                              <td className="sticky left-[200px] z-10 bg-gray-100 border-r border-t px-2 py-2 text-right min-w-[60px]">
                                {grandTotalActuel.toFixed(0)}
                              </td>
                              <td className="sticky left-[260px] z-10 bg-gray-100 border-r border-t px-2 py-2 text-right min-w-[60px]">
                                {grandTotalImpact.toFixed(0)}
                              </td>
                              <td className="sticky left-[320px] z-10 bg-gray-100 border-r border-t px-2 py-2 text-right font-semibold min-w-[60px]">
                                {grandTotalNouveau.toFixed(0)}
                              </td>
                            </React.Fragment>
                          );
                        })()}
                        {getAllDates().map((date, dateIndex) => {
                          let totalActuel = 0;
                          let totalImpact = 0;
                          
                          preview.results.forEach((series: OutstockPreviewSeries) => {
                            if (!series.is_main) {
                              const originalPoint = series.original.find(p => p.date === date);
                              if (originalPoint) {
                                totalActuel += originalPoint.value;
                              }
                              const impact = corrections[series.article_id]?.[date] ?? 0;
                              totalImpact += impact;
                            }
                          });
                          
                          const totalNouveau = totalActuel + totalImpact;
                          const isLastMonth = dateIndex === getAllDates().length - 1;
                          
                          return (
                            <React.Fragment key={date}>
                              <td className="border-r border-t px-2 py-2 text-right">{totalActuel.toFixed(0)}</td>
                              <td className="border-r border-t px-2 py-2 text-right">{totalImpact.toFixed(0)}</td>
                              <td className={`border-t px-2 py-2 text-right ${isLastMonth ? 'border-r' : 'border-r-2 border-r-gray-300'}`}>{totalNouveau.toFixed(0)}</td>
                            </React.Fragment>
                          );
                        })}
                      </tr>

                      {/* Individual Substitute Rows */}
                      {preview.results.map((series: OutstockPreviewSeries) => {
                        if (series.is_main) return null;
                        
                        return (
                          <tr key={series.article_id} className="bg-blue-50 hover:bg-blue-100">
                            <td className="sticky left-0 z-10 bg-blue-50 hover:bg-blue-100 border-r px-4 py-3 font-medium">
                              {series.article_id}
                            </td>
                            {(() => {
                              const totalActuel = series.original.reduce((sum, p) => sum + p.value, 0);
                              const totalImpact = Object.values(corrections[series.article_id] || {}).reduce((sum, v) => sum + v, 0);
                              const totalNouveau = totalActuel + totalImpact;
                              return (
                                <React.Fragment>
                                  <td className="sticky left-[200px] z-10 bg-blue-50 hover:bg-blue-100 border-r px-2 py-2 text-right min-w-[60px]">
                                    {totalActuel.toFixed(0)}
                                  </td>
                                  <td className="sticky left-[260px] z-10 bg-blue-50 hover:bg-blue-100 border-r px-2 py-2 text-right min-w-[60px]">
                                    {totalImpact.toFixed(0)}
                                  </td>
                                  <td className="sticky left-[320px] z-10 bg-blue-50 hover:bg-blue-100 border-r px-2 py-2 text-right font-semibold min-w-[60px]">
                                    {totalNouveau.toFixed(0)}
                                  </td>
                                </React.Fragment>
                              );
                            })()}
                            {getAllDates().map((date, dateIndex) => {
                              const originalPoint = series.original.find(p => p.date === date);
                              const actuel = originalPoint?.value || 0;
                              const impact = corrections[series.article_id]?.[date] ?? 0;
                              const nouveau = actuel + impact;
                              const isLastMonth = dateIndex === getAllDates().length - 1;
                              
                              return (
                                <React.Fragment key={date}>
                                  <td className="border-r px-2 py-2 text-right">{actuel.toFixed(0)}</td>
                                  <td className="border-r px-2 py-2">
                                    <input
                                      type="number"
                                      className="w-full text-right border rounded px-2 py-1 text-sm bg-white min-w-[80px]"
                                      value={impact || ''}
                                      onChange={(e) => handleCorrectionChange(series.article_id, date, parseFloat(e.target.value) || 0)}
                                      step="1"
                                    />
                                  </td>
                                  <td className={`px-2 py-2 text-right font-medium ${isLastMonth ? 'border-r' : 'border-r-2 border-r-gray-300'}`}>{nouveau.toFixed(0)}</td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {step === 'confirm' && (
            <div className="text-center py-6">
              <div className="text-green-700 font-medium">Déclaration de rupture sauvegardée avec succès.</div>
            </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
            <div className="text-sm text-gray-500">
              {loading ? 'Chargement…' : ''}
            </div>
            <div className="flex items-center gap-2">
              <button className="px-3 py-2 rounded border" onClick={handleCloseAttempt}>Fermer</button>
              {step === 'similar' && (
                <button className="px-3 py-2 rounded bg-blue-600 text-white" onClick={() => setStep('period')} disabled={loading}>Suivant</button>
              )}
              {step === 'period' && (
                <button className="px-3 py-2 rounded bg-blue-600 text-white" onClick={goToPreview} disabled={loading}>Aperçu</button>
              )}
              {step === 'preview' && (
                <>
                  <button className="px-3 py-2 rounded border border-gray-300 hover:bg-gray-50" onClick={handleReset} disabled={loading}>
                    Réinitialisation
                  </button>
                  <button className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700" onClick={handleConfirm} disabled={loading}>
                    Validation
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Close confirmation dialog */}
      {showCloseConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md">
            <h3 className="text-lg font-semibold mb-4">Confirmer la fermeture</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir fermer cette fenêtre ? Les modifications non sauvegardées seront perdues.
            </p>
            <div className="flex items-center gap-3 justify-end">
              <button 
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
                onClick={cancelClose}
              >
                Annuler
              </button>
              <button 
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                onClick={confirmClose}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OutstockModal;



